import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Search, Loader2, Tag, Sparkles, Clock, Image as ImageIcon,
  Upload, X, Trash2, Calendar,
} from 'lucide-react';
import { authHeaders } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const FILTERS = [
  { value: 'all',          label: 'All Products' },
  { value: 'offers',       label: "Today's Offers" },
  { value: 'new_arrivals', label: 'New Arrivals' },
  { value: 'both',         label: 'Offer + New Arrival' },
  { value: 'none',         label: 'Neither' },
];

function fmtPrice(v) {
  if (v == null || v === '') return '—';
  return '₹' + Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 });
}

function isoToLocalInput(iso) {
  // Converts the ISO string we store into the value HTML datetime-local needs (YYYY-MM-DDTHH:mm).
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ''; }
}

function localInputToIso(local) {
  if (!local) return null;
  try {
    const d = new Date(local);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString();
  } catch { return null; }
}

export default function OGOffersPublishing() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ offers_live: 0, new_arrivals: 0, expired: 0, offers_total_raw: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [editingLabelId, setEditingLabelId] = useState(null);
  const [editingLabelVal, setEditingLabelVal] = useState('');
  const [editingExpiryId, setEditingExpiryId] = useState(null);
  const [editingExpiryVal, setEditingExpiryVal] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const fileInputRef = useRef(null);
  const uploadTargetRef = useRef(null);

  const load = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const qs = new URLSearchParams();
      qs.set('filter', filter);
      if (search.trim()) qs.set('search', search.trim());
      const res = await axios.get(`${API}/admin/og-offers?${qs}`, { headers: authHeaders() });
      setItems(res.data.items || []);
      setStats(res.data.stats || {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load offers');
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [filter, search]);

  useEffect(() => { load(); }, [load]);

  const allSelected = items.length > 0 && items.every(p => selected.has(p.id));

  const patchOne = useCallback(async (productId, body, successMsg) => {
    setSavingId(productId);
    try {
      const res = await axios.patch(`${API}/admin/products/${productId}/og-offer`,
        body, { headers: authHeaders() });
      const fresh = res.data?.product;
      if (fresh) {
        setItems(prev => prev.map(p => p.id === productId ? { ...p, ...fresh } : p));
      }
      // Re-fetch stats but keep current view scroll
      const sQs = new URLSearchParams({ filter, limit: '1' });
      try {
        const s = await axios.get(`${API}/admin/og-offers?${sQs}`, { headers: authHeaders() });
        setStats(s.data.stats || {});
      } catch { /* non-blocking */ }
      if (successMsg) toast.success(successMsg);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSavingId(null);
    }
  }, [filter]);

  const toggleOffer = (p) => patchOne(p.id, { og_on_offer: !p.og_on_offer_raw },
    !p.og_on_offer_raw ? `"${p.name}" added to Today's Offers` : `"${p.name}" removed from Offers`);
  const toggleNewArrival = (p) => patchOne(p.id, { og_new_arrival: !p.og_new_arrival },
    !p.og_new_arrival ? `"${p.name}" marked New Arrival` : `"${p.name}" removed from New Arrivals`);

  const saveLabel = (p) => {
    const next = (editingLabelVal || '').trim();
    setEditingLabelId(null);
    if (next === (p.og_offer_label || '')) return;
    patchOne(p.id, { og_offer_label: next }, 'Offer label updated');
  };

  const saveExpiry = (p) => {
    const iso = localInputToIso(editingExpiryVal);
    setEditingExpiryId(null);
    if ((iso || null) === (p.og_offer_expires_at || null)) return;
    patchOne(p.id, { og_offer_expires_at: iso }, iso ? 'Expiry set' : 'Expiry cleared');
  };

  const clearExpiry = (p) => {
    setEditingExpiryId(null);
    patchOne(p.id, { og_offer_expires_at: null }, 'Expiry cleared');
  };

  const onClickUpload = (productId) => {
    uploadTargetRef.current = productId;
    fileInputRef.current?.click();
  };

  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    const productId = uploadTargetRef.current;
    if (!file || !productId) return;
    setUploadingId(productId);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await axios.post(
        `${API}/admin/products/${productId}/og-offer-image`, fd,
        { headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' } },
      );
      const fresh = res.data?.product;
      if (fresh) setItems(prev => prev.map(p => p.id === productId ? { ...p, ...fresh } : p));
      toast.success('Banner image uploaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingId(null);
      uploadTargetRef.current = null;
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeBanner = async (p) => {
    if (!window.confirm(`Remove banner for "${p.name}"? OG App will fall back to the product thumbnail.`)) return;
    setUploadingId(p.id);
    try {
      const res = await axios.delete(`${API}/admin/products/${p.id}/og-offer-image`,
        { headers: authHeaders() });
      const fresh = res.data?.product;
      if (fresh) setItems(prev => prev.map(x => x.id === p.id ? { ...x, ...fresh } : x));
      toast.success('Banner removed');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Remove failed');
    } finally {
      setUploadingId(null);
    }
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(items.map(p => p.id)));
  };

  const bulkAction = async (action, extraBody = {}) => {
    if (selected.size === 0) { toast.error('Select at least one product'); return; }
    setBulkBusy(true);
    try {
      const res = await axios.post(`${API}/admin/og-offers/bulk`,
        { product_ids: Array.from(selected), action, ...extraBody },
        { headers: authHeaders() });
      toast.success(`${res.data.updated} product(s) updated`);
      setSelected(new Set());
      await load(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk action failed');
    } finally {
      setBulkBusy(false);
    }
  };

  const statCards = useMemo(() => ([
    { key: 'offers_live', label: "Today's Offers Live", value: stats.offers_live ?? 0,
      Icon: Tag, accent: 'text-rose-700', bg: 'text-rose-600' },
    { key: 'new_arrivals', label: 'New Arrivals', value: stats.new_arrivals ?? 0,
      Icon: Sparkles, accent: 'text-emerald-700', bg: 'text-emerald-600' },
    { key: 'expired', label: 'Expired Offers', value: stats.expired ?? 0,
      Icon: Clock, accent: 'text-amber-700', bg: 'text-amber-600' },
  ]), [stats]);

  return (
    <div className="space-y-6" data-testid="og-offers-publishing-page">
      {/* hidden file input shared by every Upload Banner button */}
      <input ref={fileInputRef} type="file" accept="image/*"
        className="hidden" onChange={onFileChosen}
        data-testid="og-offer-image-input" />

      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <Tag className="w-7 h-7 text-rose-600" />
          Offers & New Arrivals
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Control the <span className="font-semibold text-slate-700">Today&apos;s Offers</span> and
          <span className="font-semibold text-slate-700"> New Arrivals</span> carousels on the OG Retailer App home screen.
          Expired offers auto-hide — the timestamp is the source of truth.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {statCards.map(({ key, label, value, Icon, accent, bg }) => (
          <Card key={key} data-testid={`og-offer-stat-${key}`}>
            <CardContent className="p-4 flex items-start justify-between">
              <div>
                <p className={`text-xs uppercase tracking-wide font-semibold ${accent}`}>{label}</p>
                <p className="text-3xl font-bold mt-1">{value}</p>
              </div>
              <Icon className={`w-8 h-8 ${bg} opacity-70`} />
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by product name, SKU or barcode..."
            className="pl-8" data-testid="og-offer-search" />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-52" data-testid="og-offer-filter-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {FILTERS.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {selected.size > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className="text-rose-700 border-rose-300">
              {selected.size} selected
            </Badge>
            <Button size="sm" variant="outline" disabled={bulkBusy}
              onClick={() => bulkAction('set_offer')}
              className="text-rose-700 border-rose-300 hover:bg-rose-50"
              data-testid="og-offer-bulk-set-offer">
              <Tag className="w-3.5 h-3.5 mr-1" />Mark as Offer
            </Button>
            <Button size="sm" variant="outline" disabled={bulkBusy}
              onClick={() => bulkAction('set_new_arrival')}
              className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
              data-testid="og-offer-bulk-set-arrival">
              <Sparkles className="w-3.5 h-3.5 mr-1" />Mark New Arrival
            </Button>
            <Button size="sm" variant="outline" disabled={bulkBusy}
              onClick={() => bulkAction('clear_offer')}
              data-testid="og-offer-bulk-clear">
              <X className="w-3.5 h-3.5 mr-1" />Clear Offers
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
              Clear selection
            </Button>
          </div>
        )}
      </div>

      {/* Table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Products</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-slate-500 py-10 text-center">Loading…</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-slate-500 py-12 text-center" data-testid="og-offer-empty">
              No products match the current filter.
            </p>
          ) : (
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-700 text-white">
                  <tr>
                    <th className="px-3 py-2.5 w-10">
                      <Checkbox checked={allSelected} onCheckedChange={toggleSelectAll}
                        data-testid="og-offer-select-all" />
                    </th>
                    <th className="px-3 py-2.5 text-left text-xs uppercase tracking-wide">Image</th>
                    <th className="px-3 py-2.5 text-left text-xs uppercase tracking-wide">Product</th>
                    <th className="px-3 py-2.5 text-left text-xs uppercase tracking-wide">Banner</th>
                    <th className="px-3 py-2.5 text-left text-xs uppercase tracking-wide">Offer</th>
                    <th className="px-3 py-2.5 text-left text-xs uppercase tracking-wide">Label</th>
                    <th className="px-3 py-2.5 text-left text-xs uppercase tracking-wide">Expires</th>
                    <th className="px-3 py-2.5 text-left text-xs uppercase tracking-wide">New Arrival</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(p => {
                    const isSaving = savingId === p.id;
                    const isUploading = uploadingId === p.id;
                    return (
                      <tr key={p.id} className="border-t hover:bg-slate-50"
                        data-testid={`og-offer-row-${p.id}`}>
                        <td className="px-3 py-2.5">
                          <Checkbox checked={selected.has(p.id)}
                            onCheckedChange={() => toggleSelect(p.id)}
                            data-testid={`og-offer-select-${p.id}`} />
                        </td>
                        <td className="px-3 py-2.5">
                          {p.product_image ? (
                            <img src={p.product_image} alt="" className="w-10 h-10 rounded object-cover border" />
                          ) : (
                            <div className="w-10 h-10 rounded bg-slate-100 flex items-center justify-center">
                              <ImageIcon className="w-4 h-4 text-slate-300" />
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2.5 max-w-[260px]">
                          <p className="font-medium truncate">{p.name}</p>
                          <p className="text-xs text-slate-500 truncate">
                            {p.sku || p.product_id || '—'} · {p.category || 'Uncategorised'} · MRP {fmtPrice(p.mrp)}
                          </p>
                        </td>
                        <td className="px-3 py-2.5">
                          {p.og_offer_image ? (
                            <div className="flex items-center gap-1">
                              <img src={p.og_offer_image} alt="" className="w-10 h-10 rounded object-cover border-2 border-rose-300" />
                              <Button size="sm" variant="ghost"
                                className="text-slate-400 hover:text-rose-600 px-1"
                                disabled={isUploading}
                                onClick={() => removeBanner(p)}
                                data-testid={`og-offer-banner-remove-${p.id}`}>
                                {isUploading
                                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                  : <Trash2 className="w-3.5 h-3.5" />}
                              </Button>
                            </div>
                          ) : (
                            <Button size="sm" variant="outline"
                              className="border-dashed text-slate-500 hover:text-rose-700 hover:border-rose-300"
                              disabled={isUploading}
                              onClick={() => onClickUpload(p.id)}
                              data-testid={`og-offer-banner-upload-${p.id}`}>
                              {isUploading
                                ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                                : <Upload className="w-3.5 h-3.5 mr-1" />}
                              Upload
                            </Button>
                          )}
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="flex items-center gap-2">
                            <Switch checked={!!p.og_on_offer_raw}
                              disabled={isSaving}
                              onCheckedChange={() => toggleOffer(p)}
                              data-testid={`og-offer-toggle-${p.id}`} />
                            {p.og_offer_expired
                              ? <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-[10px]">Expired</Badge>
                              : p.og_on_offer
                                ? <Badge className="bg-rose-100 text-rose-800 border-rose-300 text-[10px]">Live</Badge>
                                : null}
                          </div>
                        </td>
                        <td className="px-3 py-2.5 max-w-[160px]">
                          {editingLabelId === p.id ? (
                            <Input value={editingLabelVal}
                              onChange={(e) => setEditingLabelVal(e.target.value)}
                              onBlur={() => saveLabel(p)}
                              onKeyDown={(e) => { if (e.key === 'Enter') saveLabel(p); if (e.key === 'Escape') setEditingLabelId(null); }}
                              autoFocus placeholder="e.g. Buy 5 get 1 free"
                              className="h-7 text-xs"
                              data-testid={`og-offer-label-input-${p.id}`} />
                          ) : (
                            <button className="text-xs text-slate-700 hover:bg-slate-100 px-2 py-1 rounded truncate w-full text-left min-h-[1.75rem]"
                              onClick={() => { setEditingLabelId(p.id); setEditingLabelVal(p.og_offer_label || ''); }}
                              data-testid={`og-offer-label-${p.id}`}>
                              {p.og_offer_label || <span className="text-slate-400 italic">+ Add label</span>}
                            </button>
                          )}
                        </td>
                        <td className="px-3 py-2.5">
                          {editingExpiryId === p.id ? (
                            <div className="flex items-center gap-1">
                              <Input type="datetime-local" value={editingExpiryVal}
                                onChange={(e) => setEditingExpiryVal(e.target.value)}
                                onBlur={() => saveExpiry(p)}
                                onKeyDown={(e) => { if (e.key === 'Enter') saveExpiry(p); if (e.key === 'Escape') setEditingExpiryId(null); }}
                                autoFocus className="h-7 text-xs w-40"
                                data-testid={`og-offer-expiry-input-${p.id}`} />
                              <Button size="sm" variant="ghost" onClick={() => clearExpiry(p)}
                                className="px-1 text-rose-500"
                                data-testid={`og-offer-expiry-clear-${p.id}`}>
                                <X className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          ) : (
                            <button
                              className="text-xs text-slate-700 hover:bg-slate-100 px-2 py-1 rounded inline-flex items-center gap-1 min-h-[1.75rem]"
                              onClick={() => { setEditingExpiryId(p.id); setEditingExpiryVal(isoToLocalInput(p.og_offer_expires_at)); }}
                              data-testid={`og-offer-expiry-${p.id}`}>
                              <Calendar className="w-3 h-3 text-slate-400" />
                              {p.og_offer_expires_at
                                ? new Date(p.og_offer_expires_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })
                                : <span className="text-slate-400 italic">No expiry</span>}
                            </button>
                          )}
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="flex items-center gap-2">
                            <Switch checked={!!p.og_new_arrival}
                              disabled={isSaving}
                              onCheckedChange={() => toggleNewArrival(p)}
                              data-testid={`og-arrival-toggle-${p.id}`} />
                            {p.og_new_arrival && (
                              <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-[10px]">
                                New
                              </Badge>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-slate-400 text-center">
        Tip — click a label or expiry cell to edit inline (Enter = save, Esc = cancel).
        Banner image overrides the product thumbnail only in the OG App&apos;s Offers carousel.
      </p>
    </div>
  );
}
